<template>
  <div
    class="steps-bar"
    :class="[`steps-bar--align-${step_alignment}`, {'steps-bar--outlined': outlined}]"
    :style="steps_style"
  >
    <div
      v-for="(step, index) in steps"
      :key="step.id"
      class="steps-bar__item"
      :class="{
        'steps-bar__item--active': step.id === active_id,
        'steps-bar__item--completed': is_completed(step.id),
        'steps-bar__item--disabled': !is_enabled(step.id),
        // lo step attivo non è cliccabile (handle_click lo scarta): senza questa
        // condizione mostrerebbe cursore a mano e underline in hover per niente
        'steps-bar__item--clickable': allow_navigation && is_enabled(step.id) && step.id !== active_id,
      }"
    >
      <button
        type="button"
        class="steps-bar__group"
        :disabled="!allow_navigation || !is_enabled(step.id)"
        :aria-current="step.id === active_id ? 'step' : null"
        @click="handle_click(step)"
      >
        <!--
          ordine di rendering: se lo step dichiara una sua icona quella vince sempre,
          anche da completato (il glifo identifica il passaggio e resta leggibile);
          la spunta vale per gli step senza icona, il numero per tutti gli altri
        -->
        <span v-if="step.icon" class="steps-bar__symbol">
          <vb-icon
            :name="step.icon"
            :color="symbol_icon_color(step)"
            :size="icon_size"
          />
        </span>
        <vb-icon
          v-else-if="is_completed(step.id)"
          class="steps-bar__symbol"
          :name="'check'"
          :color="'ffffff'"
          :size="16"
        />
        <span v-else class="steps-bar__symbol">{{ index + 1 }}</span>
        <span class="steps-bar__label">{{ step.name }}</span>
      </button>
      <span
        v-if="index !== steps.length - 1"
        :class="{
          'steps-bar__line': true,
          'steps-bar__line--completed': is_completed(step.id),
        }"
      ></span>
    </div>
  </div>
</template>

<script>
  export default {
    name: 'vb-steps-bar',
    props: {
      steps: {
        type: Array,
        required: true,
      },
      active_id: {
        type: String,
        default: '',
      },
      completed_ids: {
        type: Array,
        default: () => [],
      },
      enabled_ids: {
        type: Array,
        default: null,
      },
      allow_navigation: {
        type: Boolean,
        default: true,
      },
      // pallini degli step non raggiunti in outline (bordo grigio) invece che pieni
      outlined: {
        type: Boolean,
        default: false,
      },
      customizations: {
        type: Object,
        default: () => ({}),
      },
    },
    computed: {
      step_color() {
        const { steps_color = 'var(--primary)' } = this.customizations;
        return steps_color;
      },
      step_alignment() {
        const { steps_alignment = 'row' } = this.customizations;
        return steps_alignment;
      },
      symbol_size() {
        const { symbol_size = '25px' } = this.customizations;
        return symbol_size;
      },
      line_thickness() {
        const { line_thickness = '2.5px' } = this.customizations;
        return line_thickness;
      },
      // l'icona sta a metà del pallino (nel Figma: pallino 40, icona 20).
      // symbol_size è atteso in px, come lo passa la library
      icon_size() {
        return parseInt(this.symbol_size, 10) / 2;
      },
      steps_style() {
        return {
          '--step-color': this.step_color,
          '--step-alignment': this.step_alignment,
          '--symbol-size': this.symbol_size,
          '--line-thickness': this.line_thickness,
        };
      },
    },
    methods: {
      // con i pallini in outline il glifo degli step non raggiunti è grigio,
      // altrimenti è sempre bianco perché il pallino è pieno di colore
      symbol_icon_color(step) {
        const is_reached =
          step.id === this.active_id || this.is_completed(step.id);
        if (this.outlined && !is_reached) return this.sass('gray-500');
        return 'fff';
      },
      is_completed(id) {
        return this.completed_ids.indexOf(id) >= 0;
      },
      is_enabled(id) {
        if (!this.enabled_ids) return true;
        if (id === this.active_id) return true;
        return this.enabled_ids.indexOf(id) >= 0;
      },
      handle_click(step) {
        if (!this.allow_navigation) return;
        if (step.id === this.active_id) return;
        if (!this.is_enabled(step.id)) return;
        this.$emit('select', step.id);
      },
    },
  };
</script>

<style lang="scss" scoped>

  .steps-bar {
    background-color: $white;
    border-radius: 10px;
    padding: 15px 20px;
    display: flex;
    align-items: center;
    position: relative;
    margin-bottom: $grid-gutter-width/2;

    * {
      transition: 250ms;
    }

    &__group {
      // reset native <button> chrome — the group is the focusable/clickable
      // element, but must lay out as the symbol + label flex row
      appearance: none;
      background: none;
      border: 0;
      padding: 0;
      margin: 0;
      color: inherit;
      font: inherit;
      // bootstrap dà `cursor: pointer` a ogni button non disabilitato: la mano
      // la rimette --clickable, sullo step attivo non ci deve stare
      cursor: default;
      display: flex;
      align-items: center;
      flex-direction: var(--step-alignment, row);
      gap: 10px;
      // senza una base fissa l'ultimo item si stringe sul contenuto (width:
      // min-content) e la barra non chiude alla stessa larghezza degli altri
      // contenitori della pagina
      min-width: 110px;
    }

    &__item {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: flex-start;

      &:last-child {
        width: min-content;
        justify-content: center;
        margin-right: $grid-gutter-width * 2;
      }

      &--clickable {
        .steps-bar__group {
          cursor: pointer;
        }

        .steps-bar__group:hover {
          .steps-bar__symbol {
            box-shadow: 0px 0px 7.5px var(--step-color);
          }
          .steps-bar__label {
            text-decoration: underline;
          }
        }
      }

      &--disabled {
        opacity: 0.45;
        .steps-bar__group {
          cursor: not-allowed;
        }
      }

      &--active {
        .steps-bar__label {
          text-decoration: underline;
        }
        .steps-bar__symbol {
          box-shadow: 0px 0px 7.5px var(--step-color);
        }
      }
    }

    &__symbol {
      background-color: var(--step-color);
      border-radius: 50%;
      min-width: var(--symbol-size, 25px);
      min-height: var(--symbol-size, 25px);
      max-width: var(--symbol-size, 25px);
      max-height: var(--symbol-size, 25px);
      color: $white;
      font-size: $font-size-md;
      font-weight: $vb-font-weight-semibold;
      display: flex;
      align-items: center;
      justify-content: center;

      .steps-bar__item--completed & {
        background-color: var(--step-color);
      }
    }

    &__label {
      font-weight: $vb-font-weight-semibold;
    }

    &__line {
      margin-right: $grid-gutter-width/3;
      margin-left: $grid-gutter-width/3;
      min-height: var(--line-thickness, 2.5px);
      flex-grow: 1;
      background-color: $gray-300;
      &--completed {
        background-color: var(--step-color);
      }
    }

    // variante outline: solo gli step raggiunti hanno il pallino pieno di colore,
    // gli altri restano vuoti con bordo grigio. Niente glow né sottolineatura.
    &--outlined {
      .steps-bar__item:last-child {
        margin-right: 0;
      }

      .steps-bar__label {
        font-size: $font-size-md;
        font-weight: $font-weight-normal;
        color: $gray-600;
      }

      .steps-bar__item--active,
      .steps-bar__item--completed {
        .steps-bar__symbol {
          box-shadow: none;
        }

        .steps-bar__label {
          color: $gray-900;
          text-decoration: none;
        }
      }

      .steps-bar__item:not(.steps-bar__item--active):not(.steps-bar__item--completed) {
        .steps-bar__symbol {
          background-color: transparent;
          border: 2px solid $gray-300;
          box-shadow: 0 1px 2px -1px rgba($black, .3);
        }
      }

      // l'hover non deve reintrodurre il glow del tema di default
      .steps-bar__item--clickable .steps-bar__group:hover {
        .steps-bar__symbol {
          box-shadow: 0 1px 2px -1px rgba($black, .3);
        }
      }
    }

    // when the label stacks above/below the symbol, keep the connector line
    // level with the symbol instead of centered on the whole group
    &--align-column {
      .steps-bar__item {
        align-items: flex-start;
      }
      .steps-bar__line {
        margin-top: calc(
          (var(--symbol-size, 25px) - var(--line-thickness, 2.5px)) / 2
        );
      }
    }

    &--align-column-reverse {
      .steps-bar__item {
        align-items: flex-end;
      }
      .steps-bar__line {
        margin-bottom: calc(
          (var(--symbol-size, 25px) - var(--line-thickness, 2.5px)) / 2
        );
      }
    }
  }
</style>
